import React, { useState } from 'react';
import SwipeableModal from '../../../components/SwipeableModal';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { trackFeedbackSubmit } from '../../../hooks/useGoogleAnalytics';

const ContactSupportModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.user_metadata?.full_name || '',
    email: user?.email || '',
    category: '',
    subject: '',
    message: '',
    priority: 'normal'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const categories = [
    { value: 'technical', label: 'Technical Issue', icon: 'AlertCircle' },
    { value: 'billing', label: 'Billing & Subscription', icon: 'CreditCard' },
    { value: 'feature', label: 'Feature Request', icon: 'Lightbulb' },
    { value: 'data', label: 'Data & Privacy', icon: 'Shield' },
    { value: 'account', label: 'Account Management', icon: 'User' },
    { value: 'other', label: 'Other', icon: 'HelpCircle' }
  ];

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Call the send-contact-support edge function
      const { data, error } = await supabase?.functions?.invoke('send-contact-support', {
        body: {
          name: formData?.name,
          email: formData?.email,
          category: formData?.category,
          subject: formData?.subject,
          message: formData?.message,
          priority: formData?.priority
        }
      });

      if (error) {
        console.error('Error submitting support request:', error);
        setSubmitStatus('error');
        return;
      }

      if (!data?.success) {
        console.error('Support request failed:', data?.error);
        setSubmitStatus('error');
        return;
      }
      
      console.log('Support request submitted successfully:', data);
      setSubmitStatus('success');
      trackFeedbackSubmit(formData?.category, formData?.priority);
      
      // Close modal and reset form after 2 seconds
      setTimeout(() => {
        onClose();
        setFormData({
          name: user?.user_metadata?.full_name || '',
          email: user?.email || '',
          category: '',
          subject: '',
          message: '',
          priority: 'normal'
        });
        setSubmitStatus(null);
      }, 2000);
    } catch (error) {
      console.error('Error submitting support request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <SwipeableModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Contact Support"
      maxWidth="max-w-2xl"
      loading={isSubmitting}
      showCloseButton={true}
    >
      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Name & Email */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Your Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData?.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              placeholder="John Doe"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Email Address *
            </label>
            <input
              type="email"
              name="email"
              value={formData?.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              placeholder="john@example.com"
            />
          </div>
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Category *
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {categories?.map((cat) => (
              <button
                key={cat?.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, category: cat?.value }))}
                className={`
                  flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-left
                  ${formData?.category === cat?.value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-muted border-border hover:border-primary/30'
                  }
                `}
              >
                <Icon name={cat?.icon} size={16} />
                <span className="text-xs font-medium">{cat?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Priority */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Priority
          </label>
          <div className="flex gap-2">
            {['low', 'normal', 'high', 'urgent']?.map((priority) => (
              <button
                key={priority}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, priority }))}
                className={`
                  flex-1 px-3 py-2 rounded-lg border transition-colors capitalize text-sm font-medium
                  ${formData?.priority === priority
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-muted border-border hover:border-primary/30 text-foreground'
                  }
                `}
              >
                {priority}
              </button>
            ))}
          </div>
        </div>

        {/* Subject */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Subject *
          </label>
          <input
            type="text"
            name="subject"
            value={formData?.subject}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
            placeholder="Brief description of your issue"
          />
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Message *
          </label>
          <textarea
            name="message"
            value={formData?.message}
            onChange={handleChange}
            required
            rows={6}
            className="w-full px-4 py-2 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground resize-none"
            placeholder="Please provide as much detail as possible..."
          />
        </div>

        {/* Submit Button */}
        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium min-h-[44px] touch-manipulation active:scale-95 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !formData?.category || !formData?.subject || !formData?.message}
            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] touch-manipulation active:scale-95"
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>
        </div>

        {/* Status Messages */}
        {submitStatus === 'success' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <Icon name="CheckCircle" size={20} className="text-green-600" />
            <p className="text-sm text-green-800">Your message has been sent successfully!</p>
          </div>
        )}
        {submitStatus === 'error' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <Icon name="AlertCircle" size={20} className="text-red-600" />
            <p className="text-sm text-red-800">Failed to send message. Please try again.</p>
          </div>
        )}
      </form>
    </SwipeableModal>
  );
};

export default ContactSupportModal;